import { memo } from "react"
import {
	IconCheckOutline18,
	IconEnvelopeArrowRightOutline18,
	IconEnvelopeCheckOutline18,
	IconPlusOutline18,
} from "nucleo-ui-outline-18"

import type { ReportingQueryProps } from "../reporting-query"
import { AiResolutionRateChart } from "@/components/dashboard"
import {
	formatReportingOrigin,
	ReportingBarChart,
	ReportingHeatmap,
	ReportingKpiCard,
	ReportingMetricTrendCard,
	ReportingPipelineCard,
	ReportingRankedList,
} from "@/components/reporting/charts"
import { api } from "@/lib/api"

export const ConversationsTab = memo((props: ReportingQueryProps) => {
	const { startDate, endDate, ...filters } = props
	const query = api.workspace.getReportingConversations.useQuery(
		{ startDate, endDate, ...filters },
		{ cacheTime: Infinity, staleTime: 60_000 },
	)
	const data = query.data
	const loading = query.isLoading

	return (
		<div className="space-y-4 p-4 sm:p-6">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<ReportingKpiCard
					label="New"
					value={data?.kpi.newConversations}
					isLoading={loading}
					Icon={IconPlusOutline18}
					tone="categorical-3"
					hint="Created in range"
				/>
				<ReportingKpiCard
					label="Replied"
					value={data?.replied}
					isLoading={loading}
					Icon={IconEnvelopeCheckOutline18}
					tone="categorical-0"
					hint="Threads with a reply"
				/>
				<ReportingKpiCard
					label="Closed"
					value={data?.kpi.closed}
					isLoading={loading}
					Icon={IconCheckOutline18}
					tone="categorical-1"
					hint="Marked done"
				/>
				<ReportingKpiCard
					label="Replies sent"
					value={data?.repliesSent}
					isLoading={loading}
					Icon={IconEnvelopeArrowRightOutline18}
					tone="categorical-4"
					hint="Outbound messages"
				/>
			</div>

			<ReportingPipelineCard
				title="Pipeline"
				description="Current state mix for conversations created in this range."
				segments={[
					{
						label: "Todo",
						value: data?.pipeline.todo ?? 0,
						tone: "categorical-0",
					},
					{
						label: "Snoozed",
						value: data?.pipeline.snoozed ?? 0,
						tone: "categorical-3",
					},
					{
						label: "Done",
						value: data?.pipeline.done ?? 0,
						tone: "categorical-1",
					},
				]}
				isLoading={loading}
			/>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<ReportingBarChart
					title="New conversations per day"
					data={(data?.newPerDay ?? []).map((p) => ({
						date: p.date,
						value: p.value,
					}))}
					series={[{ key: "value", label: "New", tone: "categorical-3" }]}
					average={data?.newPerDayAverage}
					isLoading={loading}
				/>
				<AiResolutionRateChart
					startDate={startDate}
					endDate={endDate}
					{...filters}
				/>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				<ReportingMetricTrendCard
					title="Queue depth"
					value={data?.queueDepth ?? "—"}
					data={data?.queueDepthSeries ?? []}
					isLoading={loading}
					tone="categorical-2"
				/>
				<ReportingBarChart
					title="New by channel"
					data={(data?.byChannel ?? []).map((c) => ({
						date: formatReportingOrigin(c.origin),
						value: c.value,
					}))}
					series={[
						{
							key: "value",
							label: "Conversations",
							tone: "categorical-4",
						},
					]}
					isLoading={loading}
					categoryAxis
				/>
			</div>

			<ReportingHeatmap
				title="Hourly distribution of new conversations"
				description="When conversations are created across the week."
				cells={data?.heatmap ?? []}
				isLoading={loading}
			/>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				<ReportingRankedList
					title="Companies"
					items={data?.companies ?? []}
					isLoading={loading}
					tone="categorical-0"
				/>
				<ReportingRankedList
					title="Tags"
					items={data?.tags ?? []}
					isLoading={loading}
					tone="categorical-3"
				/>
				<ReportingRankedList
					title="Tiers"
					items={data?.tiers ?? []}
					isLoading={loading}
					tone="categorical-2"
				/>
			</div>
		</div>
	)
})
